import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { JackpotView } from "@/campaigns/modules/jackpot/types";
import { ceilingFraction } from "@/campaigns/modules/jackpot/types";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

/** The meter's colour: the progress green, the same token the timed ring runs in — the pot is
 *  moving toward something, and it is going well. The heat lives in the ceiling badge alone. */
const METER_COLOUR = "var(--active)";

/**
 * The idle pulse's period: a slow breath while the pot is far from its ceiling, quickening as the
 * headroom shrinks — the same "warming" the colour ramp tells, in time instead of hue.
 */
function pulseSeconds(fraction: number): number {
  return 2.4 - 1.4 * Math.min(1, Math.max(0, fraction));
}

/**
 * The pays-before meter: a track with a glowing "comet" knob riding the fill toward the ceiling
 * badge — an orange pill carrying the ceiling figure ("€500") at the end of the row — under a
 * centred line naming it ("Must pay before €500"). The rail takes whatever width the badge leaves,
 * so 100% is the badge's edge and the knob arrives *at* the amount. The pot itself is the hero
 * number above — not repeated here.
 *
 * Deliberately NOT a progress bar toward "done": a pays-before pot can be won on any spin, and the
 * ceiling is only the point by which it is guaranteed — a bar that fills to completion would tell
 * the player it happens when the bar is full. The knob is the pot's *position*; the colour is how
 * likely. Renders nothing when there is no ceiling or no live pot.
 *
 * Idle, the meter breathes: a sheen travels along the fill, the comet's glow swells and its trail
 * brightens with it, all on one period that shortens as the pot nears the ceiling. Static under
 * `prefers-reduced-motion`.
 */
export const JackpotCeilingScale = ({
  view,
  compact = false,
  className,
}: {
  view: JackpotView;
  compact?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const fraction = ceilingFraction(view);
  const max = view.jackpot.max_payout_amount;
  if (fraction === null || !max) return null;

  const colour = METER_COLOUR;
  const pct = `${Math.round(fraction * 1000) / 10}%`;
  const period = pulseSeconds(fraction);
  const loop = {
    duration: period,
    repeat: Infinity,
    ease: "easeInOut" as const,
  };

  const glowRest = `0 0 12px color-mix(in srgb, ${colour} 90%, transparent), 0 0 26px color-mix(in srgb, ${colour} 50%, transparent)`;
  const glowPeak = `0 0 16px color-mix(in srgb, ${colour} 100%, transparent), 0 0 38px color-mix(in srgb, ${colour} 70%, transparent)`;

  return (
    <div className={cn("flex w-full flex-col items-center gap-2", className)}>
      <span
        className={cn(
          "text-center font-heading font-bold",
          compact ? "text-[13px] leading-4" : "text-sm leading-[18px]"
        )}
        style={{ color: colour }}
      >
        {t("jackpot.mustPayBefore", "Must pay before {{amount}}", {
          amount: max.display,
        })}
      </span>

      {/* Decorative: the line above carries the meaning. */}
      <div aria-hidden className="my-1.5 flex w-full items-center gap-2.5">
        <div className="relative h-[18px] min-w-0 flex-1">
          {/* track */}
          <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 rounded-full bg-(--progress-track)" />
          {/* fill: faint at the start, solid at the knob */}
          <div
            className="absolute top-1/2 left-0 h-px -translate-y-1/2 overflow-hidden rounded-full"
            style={{
              width: pct,
              backgroundImage: `linear-gradient(to right, color-mix(in srgb, ${colour} 25%, transparent), ${colour})`,
            }}
          >
            {/* the sheen: a soft light band travelling along the fill toward the knob */}
            {!reducedMotion && (
              <motion.div
                className="absolute inset-y-0 w-[45%]"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, transparent, rgba(255,255,255,0.55), transparent)",
                }}
                initial={{ x: "-100%" }}
                animate={{ x: "320%" }}
                transition={{ ...loop, ease: "easeIn" }}
              />
            )}
          </div>
          {/* the comet's trail — never longer than the fill behind the knob, so at low values it
              does not reach back past the start of the track */}
          <motion.div
            className="absolute top-1/2 h-2 -translate-x-full -translate-y-1/2 rounded-full blur-[5px]"
            style={{
              left: pct,
              width: `min(110px, ${pct})`,
              backgroundImage: `linear-gradient(to right, transparent, color-mix(in srgb, ${colour} 80%, transparent))`,
            }}
            animate={reducedMotion ? undefined : { opacity: [0.6, 1, 0.6] }}
            transition={loop}
          />
          {/* the knob: its glow breathes, and it swells a little with each breath */}
          <motion.div
            className="absolute top-1/2 size-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ left: pct, backgroundColor: colour, boxShadow: glowRest }}
            animate={
              reducedMotion
                ? undefined
                : {
                    boxShadow: [glowRest, glowPeak, glowRest],
                    scale: [1, 1.12, 1],
                  }
            }
            transition={loop}
          />
        </div>
        {/* the ceiling: the badge the knob is heading for */}
        <Badge color="urgent" currencyMark>
          {max.display}
        </Badge>
      </div>
    </div>
  );
};
